import { ChangeEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { CircularProgress } from "@mui/material";

import { Link } from "react-router-dom";

import { faLink } from "@fortawesome/pro-regular-svg-icons";

import { InputField } from "@/components/InputField/InputField";
import { CustomColumnTypes } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";
import { runOrToast } from "@/utils/runOrToast";

import { Icon } from "@/components";

import { useUpdateCostumColumnMutation } from "@/pages/Private/redux/list/list.api";

import { ListPerson } from "../../schema/list";
import { ColumnValue } from "../../schema/columnValue";
import { ColumnConfig } from "../../schema/columnConfig";

export interface CustomTextColumnProps {
	showEdit?: { id: number; name: string };
	columnValue?: ColumnValue;
	columnConfig: ColumnConfig;
	row: ListPerson;
	customColumnTextValue?: string;
	setCustomColumnTextValue: (value: string) => void;
	setShowEdit: (value: { id: number; name: string }) => void;
	type: CustomColumnTypes;
}

export const CustomLinkColumn: FunctionComponent<CustomTextColumnProps> = ({
	showEdit,
	columnValue,
	columnConfig,
	row,
	customColumnTextValue,
	setCustomColumnTextValue,
	setShowEdit,
	type,
}) => {
	const [updatePerson, { isLoading }] = useUpdateCostumColumnMutation();
	const { t } = useTranslation();

	const testForUrl = (url: string) => {
		try {
			const correctUrl = new URL(url);

			if (correctUrl.protocol === "http:" || correctUrl.protocol === "https:") {
				return true;
			}

			return false;
		} catch {
			return false;
		}
	};

	if (isLoading) {
		return <CircularProgress />;
	}

	const currentTextValue: string = columnValue?.value || "";

	if (showEdit?.id === row.id && showEdit?.name === columnConfig.name) {
		return (
			<InputField
				autoFocus
				handleBlur={() => {
					setShowEdit({ id: 0, name: "" });
				}}
				handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
					setCustomColumnTextValue(event.target.value);
				}}
				name={columnConfig.name}
				value={customColumnTextValue || ""}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						const submitted = customColumnTextValue || "";

						// Close this cell before the save settles: `showEdit` and the draft are shared
						// by every cell, so clearing them after the await would close a cell the user
						// opened meanwhile (PREC-4840). The cell's shown value is the stored one.
						setCustomColumnTextValue("");
						setShowEdit({ id: 0, name: "" });
						void runOrToast(
							() =>
								updatePerson({
									id: row.id,
									columnId: columnConfig.id,
									customColumnName: columnConfig.name,
									value: submitted,
									customColumnType: type,
								}).unwrap(),
							t("errors.saveFailed")
						);
					}
				}}
			/>
		);
	} else {
		return (
			<div className="flex">
				{testForUrl(currentTextValue) && (
					<Link className="mr-1" target="_blank" to={currentTextValue}>
						<Icon icon={faLink} />
					</Link>
				)}
				<button
					onClick={() => {
						setCustomColumnTextValue(columnValue?.value || "");

						setShowEdit({ id: row.id, name: columnConfig.name });
					}}
				>
					{
						<div
							className={classNames(
								"h-[26px] min-w-[100px] cursor-pointer hover:bg-brand-light hover:opacity-[0.7] leading-tight hover:text-white rounded p-1",
								"text-left"
							)}
						>
							{isLoading ? customColumnTextValue : columnValue?.value || ""}
						</div>
					}
				</button>
			</div>
		);
	}
};
